import classnames from 'classnames';
import {useMemo, useState} from 'react';

import {useAnnotationModeStore} from './AnnotationModeStore';
import {isAnchoredInLang} from './annotationAnchors';
import {
  selectNotedMarksInOrder,
  selectSupplementBorderColor,
  selectVisibleRegionAnnotations
} from './annotationSelectors';
import type {Annotation} from './annotationTypes';
import {SUPPLEMENT_BORDER_CLASS_BY_COLOR, SUPPLEMENT_MARKED_CLASS} from './supplementCard';

/**
 * What a supplement's marks make of its card, one mark or many. `regionRef` names the
 * box the edge paints on, held as state because a ref is still null on the mounting
 * pass and nothing re-renders when the marks arrive with it.
 */
export function useSupplementCard(
  regionId: string,
  lang: string | null = null
): {
  regionRef: (element: HTMLDivElement | null) => void;
  /** The card's box once it has attached; null on the mounting pass. */
  region: HTMLElement | null;
  /** Every mark in the supplement's region, in the order they were written. */
  annotations: Annotation[];
  cardClassName: string;
  /** The marks carrying a note, in the order they read. */
  notedMarks: Annotation[];
} {
  const [region, setRegion] = useState<HTMLElement | null>(null);
  const storedAnnotations = useAnnotationModeStore((state) =>
    selectVisibleRegionAnnotations(state, regionId)
  );
  // The edge answers for the rendering on screen, as the paint does: a mark made on
  // the translation is not this card's to advertise.
  const annotations = useMemo(
    () => storedAnnotations.filter(({anchor}) => isAnchoredInLang(anchor, lang)),
    [storedAnnotations, lang]
  );
  const borderColor = useMemo(() => selectSupplementBorderColor(annotations), [annotations]);
  const notedMarks = useMemo(() => selectNotedMarksInOrder(annotations), [annotations]);

  return {
    regionRef: setRegion,
    region,
    annotations,
    cardClassName: classnames(
      borderColor && [SUPPLEMENT_MARKED_CLASS, SUPPLEMENT_BORDER_CLASS_BY_COLOR[borderColor]]
    ),
    notedMarks
  };
}
